import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Subscription } from "@appTypes/index";
import { useUserContext } from "@context/UserContext";
import getSubscriptions from "@api/getSubscriptions";
import { USER_TYPE_BASIC } from "@constants/user";
import { SUBSCRIPTION_PRODUCT_TYPES } from "@constants/subscriptions";

export interface SubscriptionContextType {
  subscriptions: Subscription[];
  areSubscriptionsLoading: boolean;
  error: string | null;
  refreshSubscriptions: () => void;
  updateSubscriptionLocally: (subscriptionIdentifier: string, updates: Partial<Subscription>) => void;
  shouldShowPremiumUpgradeModal: boolean;
}

const defaultContext: SubscriptionContextType = {
  subscriptions: [],
  areSubscriptionsLoading: true,
  error: null,
  refreshSubscriptions: () => undefined,
  updateSubscriptionLocally: () => undefined,
  shouldShowPremiumUpgradeModal: false
};

export const SubscriptionContext = createContext<SubscriptionContextType>(defaultContext);

export const SubscriptionContextProvider = ({ children }: { children: ReactNode }) => {
  const { user, isUserLoading } = useUserContext();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [areSubscriptionsLoading, setAreSubscriptionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refreshSubscriptions = () => setRefreshTick((t) => t + 1);

  const updateSubscriptionLocally = (subscriptionIdentifier: string, updates: Partial<Subscription>) => {
    setSubscriptions((prev) =>
      prev.map((sub) =>
        (sub.subscriptionIdentifier === subscriptionIdentifier || sub.id === subscriptionIdentifier)
          ? { ...sub, ...updates }
          : sub
      )
    );
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchSubscriptions = async () => {
      setAreSubscriptionsLoading(true);
      setError(null);

      const response = await getSubscriptions(controller.signal);

      if (response.error === "AbortError") return;

      if (response.success && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        const sorted = [...data].sort((a, b) => {
          const aIsPremium = a.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.SELF_PAID_PREMIUM ? 0 : 1;
          const bIsPremium = b.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.SELF_PAID_PREMIUM ? 0 : 1;
          return aIsPremium - bIsPremium;
        });
        setSubscriptions(sorted);
      } else {
        setError(response.error);
      }
      setAreSubscriptionsLoading(false);
    };

    fetchSubscriptions();
    return () => controller.abort();
  }, [refreshTick]);

  const shouldShowPremiumUpgradeModal = (() => {
    if (isUserLoading || areSubscriptionsLoading || !user) {
      return false;
    }

    if (user.premiumTrial) {
      return true;
    }

    const hasAnActivePremiumSubscription = subscriptions.some(sub =>
      sub.subscriptionType === SUBSCRIPTION_PRODUCT_TYPES.SELF_PAID_PREMIUM &&
      sub.autoRenew
    );

    return user.userType === USER_TYPE_BASIC && !hasAnActivePremiumSubscription;
  })();

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions,
        areSubscriptionsLoading,
        error,
        refreshSubscriptions,
        updateSubscriptionLocally,
        shouldShowPremiumUpgradeModal
      }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => useContext(SubscriptionContext);
